/**
 * Admin Sidebar Component
 */

import { useQuery } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';

import { APP_VERSION } from '../config/version';
import { cn } from '../lib/utils';
import { repairOrderService } from '../services';
import { useUIStore, useAuthStore } from '../stores';

/** Rollen, die Werkstatt-Meldungen prüfen — Spiegel von ISSUE_REVIEWER_ROLES in der API. */
const ISSUE_REVIEWER_ROLES = ['SUPER_ADMIN', 'ADMIN', 'DISPATCHER'];

interface NavItem {
  name: string;
  href: string;
  icon: React.FC<{ className?: string }>;
  badge?: number;
  roles?: string[];
}

const navigation: NavItem[] = [
  { name: 'Leitstand', href: '/', icon: DashboardIcon },
  {
    name: 'Dispatcher',
    href: '/dispatcher',
    icon: DispatcherIcon,
    roles: ['DISPATCHER', 'ADMIN', 'SUPER_ADMIN'],
  },
  {
    name: 'Yard',
    href: '/yard',
    icon: YardIcon,
    roles: ['YARD_WORKER', 'DISPATCHER', 'ADMIN', 'SUPER_ADMIN'],
  },
  { name: 'Fahrzeuge', href: '/vehicles', icon: VehicleIcon },
  {
    name: 'Check-In',
    href: '/vehicles/checkin',
    icon: CheckInIcon,
    roles: ['YARD_WORKER', 'DISPATCHER', 'ADMIN', 'SUPER_ADMIN'],
  },
  // Rollen-Queues = fokussierte Worker-Cockpits: nur der jeweiligen Worker-Rolle
  // zeigen. Admin/Dispatcher nutzen stattdessen „Services" (mit Kategorie-Filter);
  // die Queue-Routen bleiben per URL erreichbar.
  {
    name: 'Hof-Queue',
    href: '/tasks/queue',
    icon: QueueIcon,
    roles: ['YARD_WORKER'],
  },
  {
    name: 'Reinigung',
    href: '/tasks/cleaning',
    icon: CleaningIcon,
    roles: ['CLEANING'],
  },
  {
    name: 'QS-Prüfung',
    href: '/tasks/qc',
    icon: QCIcon,
    roles: ['QC_INSPECTOR'],
  },
  {
    name: 'Werkstatt',
    href: '/tasks/workshop',
    icon: WorkshopIcon,
    roles: ['WORKSHOP'],
  },
  { name: 'Services', href: '/tasks', icon: TaskIcon },
  { name: 'Workflows', href: '/workflows', icon: WorkflowIcon, roles: ['ADMIN', 'SUPER_ADMIN'] },
  {
    name: 'Service-Intervalle',
    href: '/service-intervals',
    icon: TaskTemplateIcon,
    roles: ['ADMIN', 'SUPER_ADMIN'],
  },
  {
    name: 'Service-Katalog',
    href: '/service-catalog',
    icon: TaskTemplateIcon,
    roles: ['ADMIN', 'SUPER_ADMIN'],
  },
  {
    name: 'Servicetypen',
    href: '/service-types',
    icon: TaskTemplateIcon,
    roles: ['ADMIN', 'SUPER_ADMIN'],
  },
  {
    name: 'Kunden',
    href: '/customers',
    icon: CustomerIcon,
    roles: ['DISPATCHER', 'ADMIN', 'SUPER_ADMIN'],
  },
  {
    name: 'Verträge',
    href: '/contracts',
    icon: ContractIcon,
    roles: ['DISPATCHER', 'ADMIN', 'SUPER_ADMIN'],
  },
  {
    name: 'Schäden',
    href: '/damage',
    icon: DamageIcon,
    roles: ['DISPATCHER', 'ADMIN', 'SUPER_ADMIN'],
  },
  { name: 'KVA', href: '/kvas', icon: KVAIcon, roles: ['DISPATCHER', 'ADMIN', 'SUPER_ADMIN'] },
  {
    name: 'Reparaturen',
    href: '/repair',
    icon: RepairIcon,
    roles: ['DISPATCHER', 'WORKSHOP', 'ADMIN', 'SUPER_ADMIN'],
  },
  {
    name: 'Versicherung',
    href: '/insurance',
    icon: InsuranceIcon,
    roles: ['DISPATCHER', 'ADMIN', 'SUPER_ADMIN'],
  },
  {
    name: 'Rechnungen',
    href: '/invoices',
    icon: InvoiceIcon,
    roles: ['DISPATCHER', 'ADMIN', 'SUPER_ADMIN'],
  },
  { name: 'Benutzer', href: '/users', icon: UsersIcon, roles: ['ADMIN', 'SUPER_ADMIN'] },
  {
    name: 'Berichte',
    href: '/reports',
    icon: ReportIcon,
    roles: ['DISPATCHER', 'ADMIN', 'SUPER_ADMIN'],
  },
  {
    name: 'Fahrzeugmodelle',
    href: '/settings/vehicle-models',
    icon: SettingsIcon,
    roles: ['ADMIN', 'SUPER_ADMIN'],
  },
  {
    name: 'Standorte',
    href: '/settings/locations',
    icon: LocationIcon,
    roles: ['ADMIN', 'SUPER_ADMIN'],
  },
  {
    name: 'Hof-Zonen',
    href: '/settings/yard-zones',
    icon: ZoneIcon,
    roles: ['DISPATCHER', 'ADMIN', 'SUPER_ADMIN'],
  },
  {
    name: 'Stellplätze',
    href: '/settings/yard-positions',
    icon: ParkingIcon,
    roles: ['DISPATCHER', 'ADMIN', 'SUPER_ADMIN'],
  },
  {
    name: 'System-Jobs',
    href: '/system/jobs',
    icon: TaskTemplateIcon,
    roles: ['ADMIN', 'SUPER_ADMIN'],
  },
  {
    name: 'Einstellungen',
    href: '/settings',
    icon: SettingsIcon,
    roles: ['DISPATCHER', 'ADMIN', 'SUPER_ADMIN'],
  },
];

export function Sidebar() {
  const location = useLocation();
  const { sidebarCollapsed, toggleSidebarCollapsed } = useUIStore();
  const { user, getActiveRole } = useAuthStore();

  const activeRole = getActiveRole();

  // Filter navigation based on ACTIVE role
  // This provides a focused experience - user sees menu items relevant to their current role
  // Routes are still protected by ALL user roles (security), but sidebar shows active role's items
  // Offene Werkstatt-Meldungen als Zähler an "Reparaturen" — sonst bleibt eine
  // Meldung liegen, bis jemand zufällig den passenden Auftrag öffnet.
  const canReviewIssues = !!activeRole && ISSUE_REVIEWER_ROLES.includes(activeRole);
  const { data: openIssues } = useQuery({
    queryKey: ['repair-open-issues-count'],
    queryFn: () => repairOrderService.getOpenIssues({ limit: 1 }),
    enabled: canReviewIssues,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const openIssueCount =
    (openIssues as unknown as { meta?: { total?: number } } | undefined)?.meta?.total ?? 0;

  const filteredNavigation = navigation
    .filter((item) => {
      // Items without role restrictions are shown to everyone
      if (!item.roles) return true;
      if (!user || !activeRole) return false;

      // Show item if the active role has access to it
      return item.roles.includes(activeRole);
    })
    .map((item) =>
      item.href === '/repair' && canReviewIssues && openIssueCount > 0
        ? { ...item, badge: openIssueCount }
        : item,
    );

  // Find the most specific matching nav item
  const getActiveHref = () => {
    const pathname = location.pathname;
    let bestMatch = '';
    for (const item of filteredNavigation) {
      if (item.href === '/') {
        if (pathname === '/') bestMatch = '/';
      } else if (pathname === item.href || pathname.startsWith(item.href + '/')) {
        // Only update if this is a more specific (longer) match
        if (item.href.length > bestMatch.length) {
          bestMatch = item.href;
        }
      }
    }
    return bestMatch;
  };

  const activeHref = getActiveHref();

  return (
    <aside
      className={cn(
        'flex flex-col bg-gray-900 text-white transition-all duration-300',
        sidebarCollapsed ? 'w-20' : 'w-64',
      )}
    >
      {/* Logo */}
      <div className="flex items-center h-16 px-4 border-b border-gray-800">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg
              className="w-5 h-5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
          </div>
          {!sidebarCollapsed && (
            <div className="flex flex-col">
              <span className="text-lg font-bold text-purple-400">FleetTracko</span>
              <span className="text-xs text-gray-400">Compound Admin</span>
            </div>
          )}
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3">
        <ul className="space-y-1">
          {filteredNavigation.map((item) => {
            // Only highlight the most specific matching route
            const isActive = item.href === activeHref;

            return (
              <li key={item.name}>
                <Link
                  to={item.href}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white',
                  )}
                  title={sidebarCollapsed ? item.name : undefined}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  {!sidebarCollapsed && (
                    <>
                      <span className="flex-1">{item.name}</span>
                      {item.badge !== undefined && item.badge > 0 && (
                        <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                          {item.badge > 99 ? '99+' : item.badge}
                        </span>
                      )}
                    </>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Version + Collapse button */}
      <div className="p-3 border-t border-gray-800">
        {!sidebarCollapsed && (
          <p className="text-xs text-gray-500 text-center mb-2">v{APP_VERSION}</p>
        )}
        <button
          onClick={toggleSidebarCollapsed}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
        >
          {sidebarCollapsed ? (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 5l7 7-7 7M5 5l7 7-7 7"
              />
            </svg>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
                />
              </svg>
              <span className="text-sm">Einklappen</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}

// Icons
function DashboardIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
      />
    </svg>
  );
}

function DispatcherIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
      />
    </svg>
  );
}

function YardIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
      />
    </svg>
  );
}

function VehicleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0"
      />
    </svg>
  );
}

function TaskIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
      />
    </svg>
  );
}

function QueueIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 6h16M4 10h16M4 14h16M4 18h16"
      />
    </svg>
  );
}

function CheckInIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function CleaningIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
      />
    </svg>
  );
}

function QCIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
      />
    </svg>
  );
}

function WorkshopIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  );
}

function WorkflowIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 10V3L4 14h7v7l9-11h-7z"
      />
    </svg>
  );
}

function CustomerIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
      />
    </svg>
  );
}

function ContractIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
      />
    </svg>
  );
}

function ReportIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
      />
    </svg>
  );
}

function SettingsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  );
}

function InvoiceIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );
}

function KVAIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
      />
    </svg>
  );
}

function DamageIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  );
}

function RepairIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z"
      />
    </svg>
  );
}

function InsuranceIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
      />
    </svg>
  );
}

function ZoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
      />
    </svg>
  );
}

function LocationIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  );
}

function ParkingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 3v18M5 3h9a4 4 0 010 8H5m0 0v10"
      />
    </svg>
  );
}

function TaskTemplateIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2"
      />
    </svg>
  );
}
